import { useTranslation } from 'react-i18next';
import { privacyArticles } from '@/mocks/privacyData';

const ARTICLE_IDS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'];

export default function PrivacyContent() {
  const { t } = useTranslation();

  const scrollToArticle = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Table of Contents */}
      <section className="bg-background-50 py-16">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-[0.06em] mb-2">
            {t('privacy.toc.heading')}
          </h2>
          <p className="text-accent-500 text-[13px] md:text-[14px] tracking-[0.12em] mb-5 font-label">
            CONTENTS
          </p>
          <div className="w-8 h-[1px] bg-accent-500/40 mb-8"></div>

          <ol className="space-y-2">
            {privacyArticles.map((article, idx) => {
              const articleId = ARTICLE_IDS[idx] ?? String(article.number).padStart(2, '0');
              return (
                <li key={article.id}>
                  <button
                    onClick={() => scrollToArticle(article.id)}
                    className="text-left text-[16px] md:text-[17px] text-foreground-700 hover:text-accent-500 transition-colors tracking-[0.04em] leading-relaxed cursor-pointer border-b border-background-200/60 pb-2 w-full"
                  >
                    <span className="text-accent-500 font-label mr-3">{article.number}.</span>
                    {t(`privacy.a.${articleId}.title`, { defaultValue: article.title })}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[760px] mx-auto px-6 md:px-8">
        <div className="border-t border-background-200/60"></div>
      </div>

      {/* Preamble */}
      <section className="bg-background-50 py-12">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <p className="text-[16px] md:text-[17px] text-foreground-700 tracking-[0.04em] leading-[2.2]">
            {t('privacy.content.preamble')}
          </p>
        </div>
      </section>

      {/* Articles */}
      {privacyArticles.map((article, idx) => {
        const articleId = ARTICLE_IDS[idx] ?? String(article.number).padStart(2, '0');
        return (
          <section key={article.id} id={article.id} className="bg-background-50 pb-14">
            <div className="max-w-[760px] mx-auto px-6 md:px-8">
              <div className="border-t border-background-200/60 pt-14">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-accent-500 text-[15px] font-label tracking-[0.08em] whitespace-nowrap">
                    {t('privacy.article.prefix')}{article.number}{t('privacy.article.suffix')}
                  </span>
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-[0.06em]">
                    {t(`privacy.a.${articleId}.title`, { defaultValue: article.title })}
                  </h2>
                </div>
                <div className="w-8 h-[1px] bg-accent-500/40 mb-6"></div>

                <div className="space-y-3">
                  {article.content.map((paragraph, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-[16px] md:text-[17px] text-foreground-700 tracking-[0.04em] leading-[2.2]"
                    >
                      {t(`privacy.a.${articleId}.p.${pIdx}`, { defaultValue: paragraph })}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Enactment Date */}
      <section className="bg-background-50 pb-20">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <div className="border-t border-background-200/60 pt-10">
            <div className="bg-background-100/70 px-6 py-5 border border-background-200/50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8">
                <span className="text-[15px] font-label text-foreground-500 tracking-[0.06em] whitespace-nowrap">
                  {t('privacy.content.enactDate')}
                </span>
                <span className="text-[16px] text-foreground-700 tracking-[0.04em]">
                  {t('privacy.content.enactDateValue')}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8 mt-3">
                <span className="text-[15px] font-label text-foreground-500 tracking-[0.06em] whitespace-nowrap">
                  {t('privacy.content.lastRevised')}
                </span>
                <span className="text-[16px] text-foreground-700 tracking-[0.04em]">
                  {t('privacy.content.lastRevisedValue')}
                </span>
              </div>
            </div>
            <p className="text-[14px] text-foreground-400 tracking-[0.04em] mt-4 leading-relaxed">
              {t('privacy.content.amendNotice')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
